import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  RateCard,
  RateCardLite,
  RateCardMutationParams,
  RateCardsApi,
  RateCardVersion,
} from 'api/rate-cards';

export const RateCardsListQueryKey = ['rate-cards'];

export type RateCardVersionsQueryKey = ['rate-card-versions', number];

export type RateCardVersionQueryKey = ['rate-card-version', number, number];

export const getRateCardVersionsQueryKey = (
  rateCardId: number,
): RateCardVersionsQueryKey => ['rate-card-versions', rateCardId];

export const getRateCardVersionQueryKey = (
  rateCardId: number,
  rateCardVersionId: number,
): RateCardVersionQueryKey => [
  'rate-card-version',
  rateCardId,
  rateCardVersionId,
];

export const useRateCardsQuery = (
  activeCardsOnly = false,
  queryOptions: UseQueryOptions<
    RateCardLite[],
    unknown,
    RateCardLite[],
    typeof RateCardsListQueryKey
  > = {},
): UseQueryResult<RateCardLite[]> =>
  useQuery(
    RateCardsListQueryKey,
    () => RateCardsApi.getRateCards(activeCardsOnly),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

export const useCreateRateCardMutation = (
  options: UseMutationOptions<RateCard, unknown, RateCard> = {},
): UseMutationResult<RateCard, unknown, RateCard> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => RateCardsApi.createRateCard(payload), {
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries(RateCardsListQueryKey);
    },
  });
};

export const useUpdateRateCardMutation = (
  options: UseMutationOptions<
    RateCardMutationParams,
    unknown,
    RateCardMutationParams
  > = {},
): UseMutationResult<
  RateCardMutationParams,
  unknown,
  RateCardMutationParams
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => RateCardsApi.updateRateCard(payload), {
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries(RateCardsListQueryKey);
    },
  });
};

export const useDeleteRateCardMutation = (
  options: UseMutationOptions<void, unknown, number> = {},
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation((rateCardId) => RateCardsApi.deleteRateCard(rateCardId), {
    ...options,
    onSuccess: (_, rateCardId) => {
      queryClient.resetQueries(getRateCardVersionsQueryKey(rateCardId));

      const rateCards = queryClient.getQueryData<RateCardLite[]>(
        RateCardsListQueryKey,
      );

      if (rateCards != null) {
        queryClient.setQueryData<RateCardLite[]>(RateCardsListQueryKey, [
          ...rateCards.filter(({ rateCardId: id }) => id !== rateCardId),
        ]);
      }
    },
  });
};

export const useRateCardVersionsQuery = (
  rateCardId?: number,
  queryOptions: UseQueryOptions<
    RateCard,
    unknown,
    RateCard,
    RateCardVersionsQueryKey
  > = {},
): UseQueryResult<RateCard> =>
  useQuery(
    getRateCardVersionsQueryKey(rateCardId as number),
    () => RateCardsApi.getRateCardVersions(rateCardId as number),
    {
      ...queryOptions,
      enabled: !!rateCardId,
    },
  );

export const useRateCardVersionQuery = (
  rateCardId?: number,
  rateCardVersionId?: number,
  queryOptions: UseQueryOptions<
    RateCardVersion,
    unknown,
    RateCardVersion,
    RateCardVersionQueryKey
  > = {},
): UseQueryResult<RateCardVersion> =>
  useQuery(
    getRateCardVersionQueryKey(
      rateCardId as number,
      rateCardVersionId as number,
    ),
    () =>
      RateCardsApi.getRateCardVersion(
        rateCardId as number,
        rateCardVersionId as number,
      ),
    {
      ...queryOptions,
      enabled: !!(rateCardId && rateCardVersionId),
    },
  );

export const useCreateRateCardVersionMutation = (
  options: UseMutationOptions<RateCardVersion, unknown, RateCardVersion> = {},
): UseMutationResult<RateCardVersion, unknown, RateCardVersion> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => RateCardsApi.createRateCardVersion(payload), {
    ...options,
    onSuccess: (data) => {
      // const rateCard = queryClient.getQueryData<RateCard>(
      //   getRateCardVersionsQueryKey(payload.rateCardId),
      // );

      // if (rateCard != null) {
      //   const updatedRateCard: RateCard = {
      //     ...rateCard,
      //     rateCardVersions: [data, ...rateCard.rateCardVersions],
      //   };

      //   queryClient.setQueryData<RateCard>(
      //     getRateCardVersionsQueryKey(payload.rateCardId),
      //     updatedRateCard,
      //   );
      // }
      queryClient.invalidateQueries(RateCardsListQueryKey);
      queryClient.resetQueries(getRateCardVersionsQueryKey(data.rateCardId));
    },
  });
};

export const useUpdateRateCardVersionMutation = (
  options: UseMutationOptions<RateCardVersion, unknown, RateCardVersion> = {},
): UseMutationResult<RateCardVersion, unknown, RateCardVersion> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => RateCardsApi.updateRateCardVersion(payload), {
    ...options,
    onSuccess: (_, payload) => {
      // const rateCard = queryClient.getQueryData<RateCard>(
      //   getRateCardVersionsQueryKey(payload.rateCardId),
      // );

      // if (rateCard != null) {
      //   const updatedRateCard: RateCard = {
      //     ...rateCard,
      //     rateCardVersions: [
      //       ...rateCard.rateCardVersions.filter(
      //         ({ rateCardVersionId: _versionId }) =>
      //           _versionId !== payload.rateCardVersionId,
      //       ),
      //       payload,
      //     ],
      //   };

      //   queryClient.setQueryData<RateCard>(
      //     getRateCardVersionsQueryKey(payload.rateCardId),
      //     updatedRateCard,
      //   );
      // }
      queryClient.invalidateQueries(RateCardsListQueryKey);
      queryClient.resetQueries(getRateCardVersionsQueryKey(payload.rateCardId));
    },
  });
};

export const useDeleteRateCardVersionMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    Pick<RateCardVersion, 'rateCardId' | 'rateCardVersionId'>
  > = {},
): UseMutationResult<
  void,
  unknown,
  Pick<RateCardVersion, 'rateCardId' | 'rateCardVersionId'>
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => RateCardsApi.deleteRateCardVersion(payload), {
    ...options,
    onSuccess: (_, { rateCardId, rateCardVersionId }) => {
      const rateCard = queryClient.getQueryData<RateCard>(
        getRateCardVersionsQueryKey(rateCardId),
      );

      if (rateCard != null) {
        const updatedRateCard: RateCard = {
          ...rateCard,
          rateCardVersions: [
            ...rateCard?.rateCardVersions.filter(
              ({ rateCardVersionId: _versionId }) =>
                _versionId !== rateCardVersionId,
            ),
          ],
        };

        queryClient.setQueryData<RateCard>(
          getRateCardVersionsQueryKey(rateCardId),
          updatedRateCard,
        );

        if (updatedRateCard.rateCardVersions.length === 0) {
          queryClient.invalidateQueries(RateCardsListQueryKey);
        }
      }
    },
  });
};
